import React, { useState, useEffect, useRef } from 'react'
import { Button, Icon, message, Avatar, Modal, Progress } from 'antd'
import {
  listAIVoices,
  uploadCustomVoice,
  getVoiceRecommendation
} from '../../../../services/api/aiVideoGeneration'
import { config } from '../../../../constants'
import { getToken } from '../../../../services/api/user'
import StepTitle from '../StepTitle'
import VoiceSelectionModal from './VoiceSelectionModal'
import './Voiceover.scss'
import { clearSceneCurrentAudio } from '../../utils/audioHistory'
import useTaskPoller from '../../hooks/useTaskPoller'
import { getLanguageLabelByCode } from '../../constants/videoLanguages'
import {
  mergeVoicesById,
  normalizeVoiceLanguages,
  normalizeVoiceList
} from './voiceUtils'

const AUDIO_PREVIEW_BASE_URL = `${config.videoGenerationUrl.replace(
  /\/?$/,
  '/'
)}audio/`

const getRecommendedVoicesCache = formData => {
  const cache = formData?.recommendedVoices

  if (Array.isArray(cache)) {
    return {
      items: cache,
      videoId: null,
      language: null,
      story: null
    }
  }

  if (cache && Array.isArray(cache.items)) {
    return cache
  }

  return null
}

const NarrationVoiceover = ({
  formData,
  setFormData,
  onValidationChange,
  voices,
  setVoices,
  onVoiceChange,
  videoId,
  isDraftMode = false,
  isClusteringAudio = false,
  audioClusteringProgress = 0,
  audioClusteringStatus = '',
  onStopGeneration,
  onRetryAudioClustering
}) => {
  const [loadingVoices, setLoadingVoices] = useState(false)
  const [voicesError, setVoicesError] = useState(null)
  const [playingVoice, setPlayingVoice] = useState(null)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [pendingVoiceId, setPendingVoiceId] = useState(null)
  const [isSelectionModalVisible, setIsSelectionModalVisible] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState(null)
  const [pendingUploadFile, setPendingUploadFile] = useState(null)
  const [pendingVoiceName, setPendingVoiceName] = useState('')
  const [pendingVoiceDescription, setPendingVoiceDescription] = useState('')
  const [pendingVoiceLanguage, setPendingVoiceLanguage] = useState('')
  const [recommendedVoices, setRecommendedVoices] = useState(
    () => getRecommendedVoicesCache(formData)?.items || []
  )
  const [loadingRecommendedVoices, setLoadingRecommendedVoices] = useState(
    false
  )
  const [recommendedVoicesError, setRecommendedVoicesError] = useState(null)
  const audioRef = useRef(null)

  const [audioProgress, setAudioProgress] = useState(0)
  const voiceListPoller = useTaskPoller({
    pollInterval: 3000,
    continuePollingOnError: false
  })

  const recommendedVoicesPoller = useTaskPoller({
    pollInterval: 3000,
    continuePollingOnError: false
  })

  useEffect(() => {
    if (voices.length === 0) {
      fetchVoices()
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(!!formData?.selectedVoice)
    }
  }, [formData?.selectedVoice])

  useEffect(() => {
    const cachedItems = getRecommendedVoicesCache(formData)?.items || []
    setRecommendedVoices(cachedItems)
  }, [formData?.recommendedVoices])

  const fetchVoices = async () => {
    setLoadingVoices(true)
    setVoicesError(null)
    try {
      const res = await listAIVoices(videoId)
      const result = await voiceListPoller.resolveTaskData(res, {
        getDirectOutput: data =>
          data && data.output !== undefined ? data.output : data,
        isDirectResponse: (data, output) => {
          const status =
            typeof data?.status === 'string' ? data.status.toLowerCase() : ''
          return (
            status === 'success' ||
            data?.success === true ||
            Array.isArray(output?.default_voices) ||
            Array.isArray(output?.uploaded_voices) ||
            Array.isArray(output?.system_voices) ||
            Array.isArray(output?.user_voices)
          )
        }
      })
      const data = result?.output
      if (!data) {
        setVoices([])
        return
      }
      const defaultVoices = normalizeVoiceList(
        data?.default_voices || data?.system_voices,
        'default'
      )
      const uploadedVoices = normalizeVoiceList(
        data?.uploaded_voices || data?.user_voices,
        'uploaded'
      )

      const allVoices = mergeVoicesById(defaultVoices, uploadedVoices)
      setVoices(allVoices)
    } catch (err) {
      setVoicesError('Unable to load voices right now.')
      message.error('Failed to load voices')
      setVoices([])
    } finally {
      setLoadingVoices(false)
    }
  }

  const fetchRecommendedVoices = async ({ force = false } = {}) => {
    const story = formData?.voiceoverText?.trim() || ''
    const language = formData.language || 'en'
    const cachedRecommendations = getRecommendedVoicesCache(formData)
    const hasValidCachedRecommendations =
      !force &&
      cachedRecommendations &&
      cachedRecommendations.videoId === videoId &&
      cachedRecommendations.language === language &&
      cachedRecommendations.story === story &&
      Array.isArray(cachedRecommendations.items)

    if (!videoId || !story) {
      setRecommendedVoices([])
      setRecommendedVoicesError(null)
      setFormData(prev => ({
        ...prev,
        recommendedVoices: null
      }))
      return
    }

    if (hasValidCachedRecommendations) {
      setRecommendedVoices(cachedRecommendations.items)
      setRecommendedVoicesError(null)
      return
    }

    setLoadingRecommendedVoices(true)
    setRecommendedVoicesError(null)

    try {
      const response = await getVoiceRecommendation({
        video_id: videoId,
        language: getLanguageLabelByCode(language),
        story
      })

      const result = await recommendedVoicesPoller.resolveTaskData(response, {
        getDirectOutput: data =>
          data && data.output !== undefined ? data.output : data,
        isDirectResponse: (data, output) => {
          const status =
            typeof data?.status === 'string' ? data.status.toLowerCase() : ''
          return (
            status === 'success' ||
            data?.success === true ||
            Array.isArray(output?.recommendations)
          )
        }
      })

      const output = result?.output || {}
      const fallbackLanguage = output?.language || null
      const rawRecommendations = Array.isArray(output?.recommendations)
        ? output.recommendations
        : []

      const normalizedRecommendedVoices = rawRecommendations
        .map((item, index) => ({
          id: item?.voice_id || `recommended-${index + 1}`,
          name: item?.voice_id || `Recommended Voice ${index + 1}`,
          type: 'recommended',
          rank: typeof item?.rank === 'number' ? item.rank : Number(index + 1),
          reasoning: item?.reasoning || '',
          description: item?.reasoning || '',
          audio_url: item?.audio_url || '',
          language: item?.language || fallbackLanguage || null,
          gender: item?.gender || null,
          age_range: item?.age_range || null,
          accent: item?.accent || null,
          languages: normalizeVoiceLanguages(
            [item?.language || fallbackLanguage].filter(Boolean)
          )
        }))
        .map(voice => {
          const matchedVoice = voices.find(
            existingVoice => existingVoice.id === voice.id
          )
          return {
            ...matchedVoice,
            ...voice,
            type: 'recommended'
          }
        })
        .sort((a, b) => {
          const rankA =
            typeof a.rank === 'number' ? a.rank : Number.MAX_SAFE_INTEGER
          const rankB =
            typeof b.rank === 'number' ? b.rank : Number.MAX_SAFE_INTEGER
          return rankA - rankB
        })

      setRecommendedVoices(normalizedRecommendedVoices)
      setFormData(prev => ({
        ...prev,
        recommendedVoices: {
          items: normalizedRecommendedVoices,
          videoId,
          language,
          story
        }
      }))
    } catch (error) {
      setRecommendedVoices([])
      setRecommendedVoicesError(
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        'Unable to load recommended voices right now.'
      )
    } finally {
      setLoadingRecommendedVoices(false)
    }
  }

  useEffect(() => {
    if (isSelectionModalVisible) {
      fetchRecommendedVoices()
    }
  }, [
    isSelectionModalVisible,
    videoId,
    formData?.voiceoverText,
    formData?.language
  ])

  const handleSelectVoice = voiceId => {
    const hasAnyAudioLink = formData?.shotList?.some(
      scene => !!scene.audio_link
    )
    if (!isDraftMode && hasAnyAudioLink) {
      setPendingVoiceId(voiceId)
      setShowConfirmationModal(true)
    } else {
      applyVoiceSelection(voiceId)
      setIsSelectionModalVisible(false)
    }
  }

  const handleOverride = () => {
    Modal.confirm({
      title: 'Confirm Override',
      content:
        'This will regenerate audio for all scenes. Are you sure you want to continue?',
      centered: true,
      okType: 'danger',
      onOk: () => {
        if (pendingVoiceId) {
          applyVoiceSelection(pendingVoiceId)
          setIsSelectionModalVisible(false)
        }
        setShowConfirmationModal(false)
        setPendingVoiceId(null)
      }
    })
  }

  const applyVoiceSelection = voiceId => {
    if (onVoiceChange) {
      onVoiceChange(voiceId, false, null)
    } else {
      setFormData(prev => ({
        ...prev,
        selectedVoice: voiceId,
        voiceoverAudio: null,
        shotList: (prev.shotList || []).map(scene => ({
          ...clearSceneCurrentAudio(scene),
          shots: scene.shots.map(shot => ({
            ...shot,
            audio_duration: null
          }))
        }))
      }))
    }
  }

  const handlePlayPreview = (voiceId, previewUrl = '') => {
    if (playingVoice === voiceId) {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      setPlayingVoice(null)
      setAudioProgress(0)
    } else {
      if (audioRef.current) {
        audioRef.current.pause()
      }
      setAudioProgress(0)
      const sourceUrl = previewUrl || `${AUDIO_PREVIEW_BASE_URL}${voiceId}`
      const audio = new Audio(sourceUrl)
      audio.ontimeupdate = () => {
        if (audio.duration) {
          setAudioProgress((audio.currentTime / audio.duration) * 100)
        }
      }
      audio.play().catch(() => message.error('Failed to play audio preview'))
      audio.onended = () => {
        setPlayingVoice(null)
        setAudioProgress(0)
      }
      audioRef.current = audio
      setPlayingVoice(voiceId)
    }
  }

  const handleUploadVoice = async options => {
    const { file } = options
    const derivedName = file?.name?.replace(/\.[^/.]+$/, '') || 'new_voice'
    setPendingUploadFile(file)
    if (!pendingVoiceName.trim()) {
      setPendingVoiceName(derivedName)
    }

    const voiceNameToUse = pendingVoiceName.trim() || derivedName
    if (!voiceNameToUse) {
      setUploadError('Add a voice name before uploading.')
      if (options?.onError) {
        options.onError(new Error('Missing required upload metadata'))
      }
      return
    }

    await uploadVoiceFile(file, voiceNameToUse, false, options)
  }

  const uploadVoiceFile = async (
    file,
    voiceName,
    overwrite = false,
    options = {}
  ) => {
    const { onSuccess, onError } = options
    if (!file) return

    const uploadFormData = new FormData()
    uploadFormData.append('voice_file', file)
    uploadFormData.append('voice_name', voiceName)
    uploadFormData.append('description', pendingVoiceDescription || '')
    if (pendingVoiceLanguage) {
      uploadFormData.append(
        'language',
        getLanguageLabelByCode(pendingVoiceLanguage)
      )
    }
    if (overwrite) {
      uploadFormData.append('overwrite', 'true')
    }

    setUploading(true)
    setUploadProgress(0)
    setUploadError(null)

    let progressInterval = null
    try {
      const token = await getToken()
      progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90))
      }, 200)

      await uploadCustomVoice(uploadFormData, token)
      setUploadProgress(100)
      message.success('Voice uploaded successfully!')
      setPendingUploadFile(null)
      setPendingVoiceName('')
      setPendingVoiceDescription('')
      setPendingVoiceLanguage('')
      if (onSuccess) onSuccess()
      await fetchVoices()
    } catch (err) {
      const detail = err?.response?.data?.detail
      const apiError = err?.response?.data?.error
      let errorMsg = 'Failed to upload voice'
      if (err.response?.status === 413) {
        errorMsg = 'File is too large. Please upload a file smaller than 50MB.'
      } else if (
        typeof detail === 'string' &&
        detail.includes('already exists')
      ) {
        errorMsg = `${detail} You can edit the voice name and retry, or overwrite existing.`
      } else if (apiError) {
        errorMsg = apiError
      } else if (detail) {
        errorMsg = detail
      } else if (err.message) {
        errorMsg = err.message
      }
      setUploadError(errorMsg)
      if (onError) onError(err)
    } finally {
      if (progressInterval) clearInterval(progressInterval)
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const beforeUpload = file => {
    setUploadError(null)
    const isLt50M = file.size / 1024 / 1024 < 50
    if (!isLt50M) {
      setUploadError('File must be smaller than 50MB!')
      return false
    }
    return true
  }

  const handleRetryUpload = async (options = {}) => {
    if (!pendingUploadFile || !pendingVoiceName?.trim()) return
    await uploadVoiceFile(
      pendingUploadFile,
      pendingVoiceName.trim(),
      false,
      options
    )
  }

  const handleOverwriteUpload = async (options = {}) => {
    if (!pendingUploadFile || !pendingVoiceName?.trim()) return
    await uploadVoiceFile(
      pendingUploadFile,
      pendingVoiceName.trim(),
      true,
      options
    )
  }

  const hasScenes = (formData?.shotList || []).length > 0
  const hasSelectedVoice = !!formData?.selectedVoice
  const hasSceneAudio = (formData?.shotList || []).some(
    scene => !!scene?.audio_link
  )
  const canRedoClustering = hasScenes && hasSelectedVoice && !isClusteringAudio

  const handleRedoAudioClustering = () => {
    if (onRetryAudioClustering) {
      onRetryAudioClustering()
    }
  }

  return (
    <div className="voiceover-step">
      {!isDraftMode && (
        <StepTitle
          iconType="sound"
          title="Narration Voice"
          description="Select a voice for your video narration."
        />
      )}

      <div className="voiceover-step__content">
        <div className="voiceover-step__mapping-card">
          <div className="voiceover-step__mapping-header">
            <div>
              <h3 className="voiceover-step__mapping-title">
                Narrator Selection
              </h3>
              <p className="voiceover-step__mapping-desc">
                Choose the main voice that will read your script.
              </p>
            </div>
          </div>

          <div className="voiceover-step__mapping-sections">
            <div className="voiceover-step__mapping-section">
              {(isClusteringAudio || hasSceneAudio) && (
                <div
                  style={{
                    marginBottom: 16,
                    padding: 16,
                    borderRadius: 12,
                    border: '1px solid #e8e8e8',
                    background: '#fafafa'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      marginBottom: isClusteringAudio ? 12 : 0,
                      flexWrap: 'wrap'
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: '#1f1f1f',
                          marginBottom: 4
                        }}
                      >
                        Scene Voice Generation
                      </div>
                      <div style={{ fontSize: 12, color: '#595959' }}>
                        {isClusteringAudio
                          ? 'Generating voice for each scene with the selected narration voice.'
                          : 'Regenerate voice for all scenes using the current narration voice.'}
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap'
                      }}
                    >
                      <Button
                        icon="reload"
                        onClick={handleRedoAudioClustering}
                        disabled={!canRedoClustering}
                      >
                        {hasSceneAudio
                          ? 'Regenerate Scene Voices'
                          : 'Retry Voice Generation'}
                      </Button>
                      {isClusteringAudio && (
                        <Button
                          icon="stop"
                          onClick={() => onStopGeneration && onStopGeneration()}
                        >
                          Stop
                        </Button>
                      )}
                    </div>
                  </div>

                  {isClusteringAudio && (
                    <>
                      <Progress
                        percent={audioClusteringProgress || 0}
                        status={
                          audioClusteringStatus === 'failed'
                            ? 'exception'
                            : 'active'
                        }
                        strokeColor="#1890ff"
                      />
                      {!!audioClusteringStatus && (
                        <div
                          style={{
                            marginTop: 8,
                            fontSize: 12,
                            color: '#595959'
                          }}
                        >
                          {audioClusteringStatus}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="voiceover-step__mapping-table-wrapper">
                <table className="voiceover-step__mapping-table">
                  <thead>
                    <tr>
                      <th>Target</th>
                      <th>Assigned Voice</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="row--active">
                      <td>
                        <div className="target-cell">
                          <Avatar
                            size={32}
                            icon="sound"
                            className="target-avatar target-avatar--active"
                          />
                          <span className="target-name">Narrator</span>
                        </div>
                      </td>
                      <td>
                        <div
                          className={`voice-badge ${!formData?.selectedVoice ? 'voice-badge--empty' : ''
                            }`}
                        >
                          {formData?.selectedVoice ? (
                            <>
                              <Icon type="check-circle" theme="filled" />
                              <span>
                                {voices.find(
                                  v => v.id === formData.selectedVoice
                                )?.name || 'Custom Voice'}
                              </span>
                            </>
                          ) : (
                            <span>No Voice Assigned</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {formData?.selectedVoice ? (
                          <span className="status-badge status-badge--mapped">
                            <Icon type="check-circle" theme="filled" />
                            Mapped
                          </span>
                        ) : (
                          <span className="status-badge status-badge--pending">
                            Unmapped
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="action-cell">
                          {formData?.selectedVoice && (
                            <Button
                              size="small"
                              shape="circle"
                              icon={
                                playingVoice === formData.selectedVoice
                                  ? 'pause'
                                  : 'caret-right'
                              }
                              onClick={() =>
                                handlePlayPreview(formData.selectedVoice)
                              }
                              className={`preview-btn ${playingVoice === formData.selectedVoice
                                  ? 'preview-btn--playing'
                                  : ''
                                }`}
                            />
                          )}
                          <Button
                            type="primary"
                            size="small"
                            className="select-btn"
                            onClick={() => setIsSelectionModalVisible(true)}
                          >
                            {formData?.selectedVoice
                              ? 'Change Voice'
                              : 'Select Voice'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <VoiceSelectionModal
        visible={isSelectionModalVisible}
        onCancel={() => setIsSelectionModalVisible(false)}
        targetName="Narrator"
        onSelect={handleSelectVoice}
        voices={voices}
        loadingVoices={loadingVoices}
        voicesError={voicesError}
        handlePlayPreview={handlePlayPreview}
        playingVoice={playingVoice}
        audioProgress={audioProgress}
        selectedVoiceId={formData?.selectedVoice}
        handleUploadVoice={handleUploadVoice}
        uploading={uploading}
        uploadProgress={uploadProgress}
        uploadError={uploadError}
        setUploadError={setUploadError}
        beforeUpload={beforeUpload}
        fetchVoices={fetchVoices}
        pendingUploadFile={pendingUploadFile}
        setPendingUploadFile={setPendingUploadFile}
        pendingVoiceName={pendingVoiceName}
        pendingVoiceDescription={pendingVoiceDescription}
        pendingVoiceLanguage={pendingVoiceLanguage}
        setPendingVoiceName={setPendingVoiceName}
        setPendingVoiceDescription={setPendingVoiceDescription}
        setPendingVoiceLanguage={setPendingVoiceLanguage}
        onRetryUpload={handleRetryUpload}
        onOverwriteUpload={handleOverwriteUpload}
        recommendedVoices={recommendedVoices}
        loadingRecommendedVoices={loadingRecommendedVoices}
        recommendedVoicesError={recommendedVoicesError}
        fetchRecommendedVoices={() => fetchRecommendedVoices({ force: true })}
        showRecommendedTab
      />

      <Modal
        title="Confirm Voice Change"
        visible={showConfirmationModal}
        onOk={handleOverride}
        onCancel={() => setShowConfirmationModal(false)}
        okText="Override & Regenerate"
        cancelText="Cancel"
      >
        <p>
          Changing the voice will require regenerating audio for all scenes. Do
          you want to proceed?
        </p>
      </Modal>
    </div>
  )
}

export default NarrationVoiceover
